import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Trash2 } from 'lucide-react'
import { useExportData, useDeleteAccount } from '@/hooks/useProfile'
import { toast } from '@/components/ui/Toast'

const C = {
  teal: '#0F5C5C',
  error: '#C85C5C',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

const card: React.CSSProperties = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: 22,
  marginBottom: 16,
}
const sectionTitle: React.CSSProperties = {
  fontFamily: FH,
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 10,
}

export default function PrivacyPage() {
  const navigate = useNavigate()
  const exportData = useExportData()
  const deleteAccount = useDeleteAccount()
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const handleDelete = () => {
    deleteAccount.mutate(undefined, {
      onSuccess: () => {
        toast.success('Tu cuenta fue eliminada')
        navigate('/')
      },
    })
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 28px 60px' }}>
      <button
        onClick={() => navigate('/profile')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: C.ink2,
          fontFamily: FB,
          fontSize: 13.5,
          marginBottom: 18,
          padding: 0,
        }}
      >
        <ArrowLeft size={16} /> Volver al perfil
      </button>

      <div
        style={{
          fontFamily: FH,
          fontWeight: 800,
          fontSize: 24,
          letterSpacing: '-0.01em',
          marginBottom: 22,
        }}
      >
        Privacidad y datos
      </div>

      <div style={card}>
        <div style={sectionTitle}>Qué datos guardamos</div>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: C.ink, lineHeight: 1.8 }}>
          <li>Cuenta: tu correo, nombre de usuario y bio</li>
          <li>Actividad: publicaciones guardadas, intereses marcados y negocios que sigues</li>
          <li>Contenido: los comentarios que publicas en las actividades</li>
        </ul>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Descarga tus datos</div>
        <p style={{ fontSize: 14, color: C.ink2, lineHeight: 1.6, marginBottom: 14 }}>
          Podés pedir una copia de todo lo que guardamos sobre tu cuenta en un archivo que podés
          abrir cuando quieras.
        </p>
        <button
          onClick={() => exportData.mutate()}
          disabled={exportData.isPending}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '11px 18px',
            borderRadius: 11,
            border: `1px solid ${C.border}`,
            background: '#fff',
            color: C.ink,
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 14,
            cursor: exportData.isPending ? 'not-allowed' : 'pointer',
            opacity: exportData.isPending ? 0.6 : 1,
          }}
        >
          <Download size={17} />
          {exportData.isPending ? 'Preparando…' : 'Descargar mis datos'}
        </button>
      </div>

      <div style={{ ...card, borderColor: confirmingDelete ? C.error : C.border }}>
        <div style={{ ...sectionTitle, color: C.error }}>Eliminar cuenta</div>
        <p style={{ fontSize: 14, color: C.ink2, lineHeight: 1.6, marginBottom: 14 }}>
          Esta acción borra tu cuenta y todo lo asociado a ella (guardados, intereses, negocios que
          sigues y comentarios) de forma permanente. No se puede deshacer.
        </p>

        {!confirmingDelete ? (
          <button
            onClick={() => setConfirmingDelete(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '11px 18px',
              borderRadius: 11,
              border: `1px solid ${C.error}`,
              background: '#fff',
              color: C.error,
              fontFamily: FB,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <Trash2 size={17} /> Eliminar mi cuenta
          </button>
        ) : (
          <div
            style={{
              background: `${C.error}0d`,
              border: `1px solid ${C.error}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <p style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 12 }}>
              ¿Seguro que querés eliminar tu cuenta? Esta acción es definitiva.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleDelete}
                disabled={deleteAccount.isPending}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: C.error,
                  color: '#fff',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: deleteAccount.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteAccount.isPending ? 0.7 : 1,
                }}
              >
                {deleteAccount.isPending ? 'Eliminando…' : 'Sí, eliminar definitivamente'}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={deleteAccount.isPending}
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: C.ink,
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
